import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, Subject } from "rxjs";
import { map } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { SaldoResumen } from "../models/Saldo/SaldoResumen";
import {
	SaldoMovimiento,
	SaldoMovimientosPaginados,
	SaldoTipoMovimiento,
} from "../models/Saldo/SaldoMovimiento";

const refrescarSaldoSubject = new Subject<void>();

export function solicitarRefrescoSaldo(): void {
	refrescarSaldoSubject.next();
}

@Injectable({ providedIn: "root" })
export class SaldoService {
	public readonly refrescar$ = refrescarSaldoSubject.asObservable();

	constructor(private http: HttpClient) {}

	public obtenerResumen(): Observable<SaldoResumen> {
		return this.http
			.get<any>(`${environment.backend_server}/saldo/resumen`)
			.pipe(map((response) => this.normalizarResumen(this.obtenerData(response))));
	}

	public obtenerMovimientos(pagina = 1, tamanoPagina = 10): Observable<SaldoMovimientosPaginados> {
		return this.http
			.get<any>(`${environment.backend_server}/saldo/movimientos`, {
				params: {
					pagina: String(pagina),
					tamanoPagina: String(tamanoPagina),
				},
			})
			.pipe(map((response) => this.normalizarMovimientos(this.obtenerData(response))));
	}

	public refrescarResumen(): void {
		solicitarRefrescoSaldo();
	}

	private obtenerData(response: any): any {
		return response?.data ?? response?.Data ?? response;
	}

	private normalizarResumen(data: any): SaldoResumen {
		return {
			tieneCuenta: Boolean(data?.tieneCuenta ?? data?.TieneCuenta ?? false),
			saldoContable: Number(data?.contable ?? data?.Contable ?? data?.saldoContable ?? data?.SaldoContable ?? 0),
			saldoReservado: Number(data?.reservado ?? data?.Reservado ?? data?.saldoReservado ?? data?.SaldoReservado ?? 0),
			saldoDisponible: Number(data?.disponible ?? data?.Disponible ?? data?.saldoDisponible ?? data?.SaldoDisponible ?? 0),
			moneda: data?.moneda ?? data?.Moneda ?? "CLP",
		};
	}

	private normalizarMovimientos(data: any): SaldoMovimientosPaginados {
		const items = data?.items ?? data?.Items ?? [];
		return {
			items: Array.isArray(items) ? items.map((item) => this.normalizarMovimiento(item)) : [],
			total: Number(data?.total ?? data?.Total ?? 0),
			pagina: Number(data?.pagina ?? data?.Pagina ?? 1),
			tamanoPagina: Number(data?.tamanoPagina ?? data?.TamanoPagina ?? 10),
		};
	}

	private normalizarMovimiento(data: any): SaldoMovimiento {
		const tipo = Number(data?.tipo ?? data?.Tipo ?? 0) as SaldoTipoMovimiento;
		const monto = Number(data?.monto ?? data?.Monto ?? 0);
		const ajusteValue = data?.ajusteEsCredito ?? data?.AjusteEsCredito;
		const ajusteEsCredito = ajusteValue === null || ajusteValue === undefined
			? null
			: Boolean(ajusteValue);

		return {
			saldoMovimientoId: data?.saldoMovimientoId ?? data?.SaldoMovimientoId ?? "",
			tipo,
			monto,
			montoConSigno: this.obtenerMontoConSigno(tipo, monto, ajusteEsCredito),
			moneda: data?.moneda ?? data?.Moneda ?? "CLP",
			ajusteEsCredito,
			origen: Number(data?.origen ?? data?.Origen ?? 0),
			claveIdempotencia: data?.claveIdempotencia ?? data?.ClaveIdempotencia ?? "",
			destino: data?.destino === null || data?.destino === undefined
				? data?.Destino === null || data?.Destino === undefined
					? null
					: Number(data.Destino)
				: Number(data.destino),
			emisorId: data?.emisorId ?? data?.EmisorId ?? "",
			empresaRazonSocial: data?.empresaRazonSocial ?? data?.EmpresaRazonSocial ?? null,
			empresaRut: data?.empresaRut ?? data?.EmpresaRut ?? null,
			cotizacionPlanId: data?.cotizacionPlanId ?? data?.CotizacionPlanId ?? null,
			ordenCompraId: data?.ordenCompraId ?? data?.OrdenCompraId ?? null,
			transaccionPagoId: data?.transaccionPagoId ?? data?.TransaccionPagoId ?? null,
			facturaOrigenId: data?.facturaOrigenId ?? data?.FacturaOrigenId ?? null,
			emisorAdicionalOrigenId: data?.emisorAdicionalOrigenId ?? data?.EmisorAdicionalOrigenId ?? null,
			certificacionOrigenId: data?.certificacionOrigenId ?? data?.CertificacionOrigenId ?? null,
			referenciaExterna: data?.referenciaExterna ?? data?.ReferenciaExterna ?? null,
			motivo: data?.motivo ?? data?.Motivo ?? null,
			fechaMovimiento: new Date(data?.fechaMovimiento ?? data?.FechaMovimiento),
		};
	}

	private obtenerMontoConSigno(
		tipo: SaldoTipoMovimiento,
		monto: number,
		ajusteEsCredito: boolean | null
	): number {
		if (tipo === SaldoTipoMovimiento.Generacion) return monto;
		if (tipo === SaldoTipoMovimiento.Ajuste) return ajusteEsCredito ? monto : -monto;
		return -monto;
	}
}
